import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

const BookContext = createContext();

export const BookProvider = ({ children }) => {
  // Book items are stored in localStorage to persist across page refreshes
  const [bookItems, setBookItems] = useState(() => {
    const saved = localStorage.getItem('bookd_book_items');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [showBookHighlight, setShowBookHighlight] = useState(false);

  // Save to localStorage whenever bookItems changes
  useEffect(() => {
    localStorage.setItem('bookd_book_items', JSON.stringify(bookItems));
  }, [bookItems]);

  // Add a bookee to the book
  const addToBook = (bookee, serviceDetails = {}) => {
    // Check if bookee is already in the book
    const exists = bookItems.find(item => item.bookee.user_id === bookee.user_id);
    if (exists) {
      toast.info(`${bookee.screen_name || bookee.name} is already in your Book`);
      return false;
    }

    const newItem = {
      id: `book_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      bookee: {
        user_id: bookee.user_id,
        name: bookee.name,
        screen_name: bookee.screen_name,
        picture: bookee.picture || bookee.portfolio_images?.[0],
        categories: bookee.categories,
        main_category: bookee.main_category,
        hourly_rate: bookee.hourly_rate,
        rating: bookee.rating,
        location: bookee.location
      },
      serviceDetails: {
        service_id: serviceDetails.service_id || null,
        date: serviceDetails.date || null,
        startTime: serviceDetails.startTime || null,
        endTime: serviceDetails.endTime || null,
        eventType: serviceDetails.eventType || '',
        eventLocation: serviceDetails.eventLocation || '',
        notes: serviceDetails.notes || '',
        pricing_type: serviceDetails.pricing_type || 'hourly',
        estimated_price: serviceDetails.estimated_price || bookee.hourly_rate || 0
      },
      status: 'pending_message', // pending_message -> messaged -> confirmed -> ready_for_checkout
      addedAt: new Date().toISOString()
    };

    setBookItems(prev => [...prev, newItem]);
    
    // Trigger highlight animation
    setShowBookHighlight(true);
    setTimeout(() => setShowBookHighlight(false), 2000);
    
    toast.success(
      <div className="flex flex-col">
        <span className="font-semibold">Added to your Book!</span>
        <span className="text-sm text-muted-foreground">
          Your Book is like a shopping cart. Message {bookee.screen_name || bookee.name} to confirm availability.
        </span>
      </div>
    );
    
    return true;
  };

  // Remove a bookee from the book
  const removeFromBook = (itemId) => {
    setBookItems(prev => prev.filter(item => item.id !== itemId));
    toast.info('Removed from your Book');
  };

  // Update item details (e.g., after messaging or setting date/time)
  const updateBookItem = (itemId, updates) => {
    setBookItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, ...updates } : item
    ));
  };

  // Update service details for an item
  const updateServiceDetails = (itemId, serviceDetails) => {
    setBookItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, serviceDetails: { ...item.serviceDetails, ...serviceDetails } } 
        : item
    ));
  };

  // Mark item as confirmed (after bookee confirms availability)
  const confirmBookItem = (itemId) => {
    updateBookItem(itemId, { status: 'confirmed' });
    toast.success('Booking confirmed! Ready for checkout.');
  };

  // Clear all items from the book
  const clearBook = () => {
    setBookItems([]);
    localStorage.removeItem('bookd_book_items');
  };

  // Get items ready for checkout (confirmed status)
  const getCheckoutReadyItems = () => {
    return bookItems.filter(item => item.status === 'confirmed');
  };

  // Get total estimated price
  const getTotalEstimatedPrice = () => {
    return bookItems.reduce((total, item) => {
      return total + (item.serviceDetails.estimated_price || 0);
    }, 0);
  };

  // Check if a bookee is already in the book
  const isInBook = (bookeeId) => {
    return bookItems.some(item => item.bookee.user_id === bookeeId);
  };

  const value = {
    bookItems,
    bookCount: bookItems.length,
    showBookHighlight,
    addToBook,
    removeFromBook,
    updateBookItem,
    updateServiceDetails,
    confirmBookItem,
    clearBook,
    getCheckoutReadyItems,
    getTotalEstimatedPrice,
    isInBook
  };

  return (
    <BookContext.Provider value={value}>
      {children}
    </BookContext.Provider>
  );
};

export const useBook = () => {
  const context = useContext(BookContext);
  if (!context) {
    throw new Error('useBook must be used within a BookProvider');
  }
  return context;
};
